"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { NewspaperIcon, CalendarIcon, UserIcon, TagIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { pickLocalizedString } from "@/lib/locale";
import { resolveIcon } from "@/lib/icon-map";
import type { LocaleCode } from "@/types/sections";
import type { NewsPageContent } from "@/types/pages";
import { buildNewsDetailPath, getLocalizedPathname } from "@/lib/site";

import { db } from "@/lib/firebase.client";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  QueryConstraint,
  DocumentData,
} from "firebase/firestore";

type Locale = LocaleCode;
type Localized<T> = Partial<Record<Locale, T>>;

type FSCategory = {
  id: string;
  name: Localized<string>;
  description?: Localized<string>;
  slug: string;
  isActive: boolean;
  sortOrder: number;
};

type FSPost = {
  _id: string;
  title: Localized<string>;
  excerpt: Localized<string>;
  author?: string;
  date: string;
  readTime?: string;
  image?: string;
  tags?: string[];
  isPublished: boolean;
  categorySlugs: string[];
};

type FSNewsDoc = {
  title: Localized<string>;
  excerpt: Localized<string>;
  author?: string;
  date: string;
  readTime?: string;
  image?: string;
  tags?: string[];
  isPublished: boolean;
  categorySlugs: string[];
};

type FSCategoryDoc = {
  name: Localized<string>;
  description?: Localized<string>;
  slug: string;
  isActive: boolean;
  sortOrder: number;
};

const CAT_COLL = "website-news-categories";
const NEWS_COLL = "website-news";
const EVENTS_COLL = "products";
const EVENT_TYPE_FIELD = "product_type";
const EVENT_TYPE_VALUE = "UNCOMMON_EVENT";
const EVENT_ACTIVE_FIELD = "active";
const EVENT_CATEGORY_SLUG = "events";

function useDebounced<T>(value: T, delay = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function stripHtml(html?: string) {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function firstNonEmpty(...vals: (string | undefined | null)[]) {
  return vals.find((v) => v && v.trim().length > 0) ?? "";
}

function mapEventToPost(doc: DocumentData): FSPost {
  const titleFr = doc.product_name as string | undefined;
  const titleEn = doc.product_name_en as string | undefined;
  const descFr = doc.product_description as string | undefined;
  const descEn = doc.product_description_en as string | undefined;
  const image = doc.image as string | undefined;
  const start = doc.start_date as string | undefined;
  const created = doc.creation_date as string | undefined;
  const dateIso = firstNonEmpty(start, created, new Date().toISOString());

  return {
    _id: `event_${doc.id}`,
    title: { fr: titleFr ?? "", en: titleEn ?? "" },
    excerpt: { fr: stripHtml(descFr), en: stripHtml(descEn) },
    author: "Uncommon 12",
    date: dateIso,
    readTime: undefined,
    image,
    tags: ["event"],
    isPublished: true,
    categorySlugs: [EVENT_CATEGORY_SLUG],
  };
}

export default function NewsPageClient({
  locale,
  content,
}: {
  locale: LocaleCode;
  content: NewsPageContent;
}) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const debouncedSearch = useDebounced(searchTerm, 250);
  const [selectedKey, setSelectedKey] = React.useState<string>("__all");

  const [categories, setCategories] = React.useState<FSCategory[]>([]);
  const [posts, setPosts] = React.useState<FSPost[]>([]);
  const [eventPosts, setEventPosts] = React.useState<FSPost[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const q = query(
      collection(db, CAT_COLL),
      where("isActive", "==", true),
      orderBy("sortOrder", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      const list: FSCategory[] = [];
      snap.forEach((d) => {
        const dd = d.data() as FSCategoryDoc;
        list.push({ id: d.id, ...dd });
      });
      setCategories(list);
    });
    return () => unsub();
  }, []);

  React.useEffect(() => {
    setLoading(true);
    const constraints: QueryConstraint[] = [
      where("isPublished", "==", true),
      orderBy("date", "desc"),
    ];
    const q = query(collection(db, NEWS_COLL), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: FSPost[] = [];
        snap.forEach((d) => {
          const dd = d.data() as FSNewsDoc;
          list.push({ _id: d.id, ...dd });
        });
        setPosts(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  React.useEffect(() => {
    const constraints: QueryConstraint[] = [
      where(EVENT_TYPE_FIELD, "==", EVENT_TYPE_VALUE),
      where(EVENT_ACTIVE_FIELD, "==", true),
    ];
    const q = query(collection(db, EVENTS_COLL), ...constraints);
    const unsub = onSnapshot(q, (snap) => {
      const list: FSPost[] = [];
      snap.forEach((d) => list.push(mapEventToPost({ id: d.id, ...d.data() })));
      setEventPosts(list);
    });
    return () => unsub();
  }, []);

  const allLabel = pickLocalizedString(
    content.categoryAllLabel,
    locale,
    locale === "fr" ? "Tous" : "All"
  );
  const eventsLabel = pickLocalizedString(
    content.categoryEventsLabel,
    locale,
    locale === "fr" ? "Événements" : "Events"
  );
  const searchPlaceholder = pickLocalizedString(
    content.searchPlaceholder,
    locale,
    locale === "fr" ? "Rechercher un article…" : "Search an article…"
  );
  const emptyLabel = pickLocalizedString(
    content.emptyLabel,
    locale,
    locale === "fr" ? "Aucun article trouvé." : "No posts found."
  );

  const categoryPills = React.useMemo(() => {
    const base = [{ key: "__all", label: allLabel }];
    const hasEventsCat = categories.some(
      (c) => c.slug.toLowerCase() === EVENT_CATEGORY_SLUG
    );
    const rest = categories.map((c) => ({
      key: c.slug,
      label: pickLocalizedString(c.name, locale) || c.slug,
    }));
    const eventsPill = hasEventsCat
      ? []
      : [
          {
            key: EVENT_CATEGORY_SLUG,
            label: eventsLabel,
          },
        ];
    return [...base, ...eventsPill, ...rest];
  }, [categories, locale, allLabel, eventsLabel]);

  const merged = React.useMemo(() => {
    return [...posts, ...eventPosts].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [posts, eventPosts]);

  const filtered = React.useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();

    return merged.filter((post) => {
      let categoryOk = true;
      if (selectedKey !== "__all") {
        const slugs = (post.categorySlugs ?? []).map((s) => s.toLowerCase());
        categoryOk = slugs.includes(selectedKey.toLowerCase());
      }

      let searchOk = true;
      if (term) {
        const hay = [
          pickLocalizedString(post.title, locale),
          pickLocalizedString(post.excerpt, locale),
          post.author ?? "",
          ...(post.tags ?? []),
        ]
          .join(" ")
          .toLowerCase();
        searchOk = hay.includes(term);
      }

      return categoryOk && searchOk;
    });
  }, [merged, selectedKey, debouncedSearch, locale]);

  const heroTitle = pickLocalizedString(content.hero.title, locale, "News");
  const heroSubtitle = pickLocalizedString(content.hero.subtitle, locale);
  const heroDescription = pickLocalizedString(content.hero.description, locale);
  const heroEyebrow = pickLocalizedString(content.hero.eyebrow, locale);
  const HeroIcon = resolveIcon(content.hero.iconKey, NewspaperIcon);
  const homeLabel = locale === "fr" ? "Accueil" : "Home";

  return (
    <div className="grid items-center justify-items-center min-h-screen font-[family-name:var(--font-geist-sans)]">
      <main className="flex flex-col row-start-2 items-center sm:items-start w-full overflow-x-hidden">
        <Breadcrumb className="mx-auto mt-[150px]">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href={getLocalizedPathname(locale, "/")}>{homeLabel}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{heroTitle}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mx-auto max-w-6xl text-center mb-12 w-fit mt-12 px-4">
          <div className="flex justify-center mb-4">
            <div className="w-fit rounded-lg border border-[#FCD2C3] p-2 bg-white/80 dark:bg-zinc-900/80">
              <HeroIcon className="h-5 w-5 text-[#FCD2C3]" />
            </div>
          </div>
          {heroEyebrow && (
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
              {heroEyebrow}
            </p>
          )}
          <h1 className="text-4xl font-bold text-black dark:text-white mb-4">{heroTitle}</h1>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
            {heroSubtitle}
          </p>
          {heroDescription && (
            <p className="text-base text-neutral-500 dark:text-neutral-400 max-w-2xl mx-auto mt-4">
              {heroDescription}
            </p>
          )}
        </div>

        <div className="w-full max-w-6xl mx-auto px-4 mb-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-4"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {categoryPills.map((c) => (
                <Button
                  key={c.key}
                  variant={selectedKey === c.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedKey(c.key)}
                  className="text-sm"
                >
                  {c.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full max-w-6xl mx-auto px-4 mb-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[320px] rounded-2xl border bg-muted/30 animate-pulse"
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border p-10 text-center text-sm text-muted-foreground">
              {emptyLabel}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((post, index) => {
                const isAboveFoldImage = index < 3;

                return (
                  <article key={post._id} className="group cursor-pointer">
                    <Link href={buildNewsDetailPath(locale, post._id, post.title)}>
                      <div className="bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 h-full">
                        <div className="aspect-video overflow-hidden relative">
                          {post.image ? (
                            <Image
                              src={post.image}
                              alt={
                                pickLocalizedString(post.title, locale) || "News image"
                              }
                              fill
                              sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                              loading={isAboveFoldImage ? "eager" : "lazy"}
                              fetchPriority={isAboveFoldImage ? "high" : "auto"}
                              className="object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full bg-muted" />
                          )}
                        </div>

                        <div className="p-6 space-y-4">
                          <div className="flex flex-wrap items-center gap-2">
                            {(post.categorySlugs ?? []).map((slug) => {
                              const cat = categories.find(
                                (c) => c.slug.toLowerCase() === slug.toLowerCase()
                              );
                              const label =
                                slug.toLowerCase() === EVENT_CATEGORY_SLUG
                                  ? eventsLabel
                                  : cat
                                  ? pickLocalizedString(cat.name, locale) || cat.slug
                                  : slug;

                              return (
                                <Badge
                                  key={slug}
                                  variant="secondary"
                                  className="bg-black/5 dark:bg-white/10 text-xs"
                                >
                                  {label}
                                </Badge>
                              );
                            })}
                          </div>

                          <div className="space-y-2">
                            <h2 className="text-xl font-bold text-black dark:text-white line-clamp-2 group-hover:text-[#FCD2C3] transition-colors">
                              {pickLocalizedString(post.title, locale)}
                            </h2>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-3">
                              {pickLocalizedString(post.excerpt, locale)}
                            </p>
                          </div>

                          <div className="flex items-center gap-4 text-xs text-neutral-500">
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              <span>
                                {new Date(post.date).toLocaleDateString(
                                  locale === "fr" ? "fr-FR" : "en-GB"
                                )}
                              </span>
                            </div>
                            {post.author && (
                              <div className="flex items-center gap-1">
                                <UserIcon className="w-3 h-3" />
                                <span>{post.author}</span>
                              </div>
                            )}
                            {post.readTime && (
                              <div className="flex items-center gap-1">
                                <TagIcon className="w-3 h-3" />
                                <span>{post.readTime}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
